/**
 * Color intention that you want to use in your theme
 * @param {JsonObject} theme Theme customization object
 */
declare module '@mui/material/styles/createPalette' {
    interface BotCardPalette {
        surface: string;
        surfaceHover: string;
        border: string;
        accent: string;
        // Header artwork, drawn as a mask over the pink tint
        pattern: string;
        tint: {
            pink: string;
            rose: string;
            lilac: string;
            violet: string;
        };
    }
    interface SurfacePalette {
        canvas: string;
        main: string;
        subtle: string;
        border: string;
        sidebar: string;
    }
    interface Palette {
        summary: PaletteColor;
        orange: PaletteColor;
        surface: SurfacePalette;
        botCard: BotCardPalette;
    }
    interface PaletteOptions {
        summary: PaletteColorOptions;
        botCard?: BotCardPalette;
        surface?: SurfacePalette;
    }
    interface ExtendedPaletteColorOptions extends SimplePaletteColorOptions {
        ultraLight: string;
        pending: string;
        initiated: string;
        denied: string;
        toRetry: string;
        unconnected: string;
        finished: string;
        bgPinkDark: string;
        bgPinkHover: string;
    }
    interface ExtendedPaletteOptions extends PaletteOptions {
        primary: ExtendedPaletteColorOptions;
        secondary: ExtendedPaletteColorOptions;
        summary: ExtendedPaletteColorOptions;
        text: Partial<TypeText>;
        error: ExtendedPaletteColorOptions;
        warning: ExtendedPaletteColorOptions;
        info: ExtendedPaletteColorOptions;
        success: ExtendedPaletteColorOptions;
    }
    interface PaletteColor {
        ultraLight: string;
        veryLight: string;
        lighter: string;
        pending: string;
        initiated: string;
        denied: string;
        toRetry: string;
        unconnected: string;
        finished: string;
        bgPinkDark: string;
        bgPinkHover: string;
        fallback: string;
    }
}

export default function themePalette(theme: any) {
    return {
        mode: theme.mode,
        common: {
            black: theme.colors?.darkPaper,
        },
        primary: {
            ultraLight: theme.colors?.primaryUltraLight,
            light: theme.colors?.primaryLight,
            main: theme.colors?.primaryMain,
            dark: theme.colors?.primaryDark,
            veryLight: theme.colors?.primary200,
            lighter: theme.colors?.primary800,
            pending: theme.colors?.summaryPending,
        },
        secondary: {
            light: theme.colors?.secondaryLight,
            main: theme.colors?.secondaryMain,
            dark: theme.colors?.secondaryDark,
            veryLight: theme.colors?.secondary200,
            lighter: theme.colors?.secondary800,
            bgPinkDark: theme.colors?.secondaryBgPinkDark,
            bgPinkHover: theme.colors?.secondaryBgPinkHover,
        },
        surface: {
            canvas: theme.colors?.canvas,
            main: theme.colors?.surface,
            subtle: theme.colors?.surfaceSubtle,
            border: theme.colors?.surfaceBorder,
            sidebar: theme.colors?.sidebar,
        },
        botCard: {
            surface: theme.colors?.botCardSurface,
            surfaceHover: theme.colors?.botCardSurfaceHover,
            border: theme.colors?.botCardBorder,
            accent: theme.colors?.botCardAccent,
            pattern: theme.colors?.botCardPinkDark,
            tint: {
                pink: theme.colors?.botCardPink,
                rose: theme.colors?.botCardRose,
                lilac: theme.colors?.botCardLilac,
                violet: theme.colors?.botCardViolet,
            },
        },
        summary: {
            pending: theme.colors?.summaryPending,
            initiated: theme.colors?.summaryInitiated,
            denied: theme.colors?.summaryDenied,
            toRetry: theme.colors?.summaryToRetry,
            unconnected: theme.colors?.summaryUnconnected,
            finished: theme.colors?.summaryFinished,
            fallback: theme.colors?.fallback,
        },
        error: {
            light: theme.colors?.errorLight,
            main: theme.colors?.errorMain,
            dark: theme.colors?.errorDark,
        },
        orange: {
            light: theme.colors?.orangeLight,
            main: theme.colors?.orangeMain,
            dark: theme.colors?.orangeDark,
        },
        warning: {
            light: theme.colors?.warningLight,
            main: theme.colors?.warningMain,
            dark: theme.colors?.warningDark,
        },
        success: {
            light: theme.colors?.successLight,
            200: theme.colors?.success200,
            main: theme.colors?.successMain,
            dark: theme.colors?.successDark,
        },
        grey: {
            50: theme.colors?.grey50,
            100: theme.colors?.grey100,
            200: theme.colors?.grey200,
            300: theme.colors?.grey300,
            500: theme.darkTextSecondary,
            600: theme.heading,
            700: theme.darkTextPrimary,
            900: theme.textDark,
        },
        dark: {
            light: theme.colors?.darkTextPrimary,
            main: theme.colors?.darkLevel1,
            dark: theme.colors?.darkLevel2,
            800: theme.colors?.darkBackground,
            900: theme.colors?.darkPaper,
        },
        text: {
            primary: theme.darkTextPrimary,
            secondary: theme.darkTextSecondary,
            dark: theme.textDark,
            hint: theme.colors?.grey100,
        },
        background: {
            paper: theme.paper,
            default: theme.backgroundDefault,
        },
    };
}
